import { FC } from "react";
import { useForm, useFormContext } from "react-hook-form";

import { ApplicationTypeEnumType } from "@libs/api/patients";
import {
  VnAccordion,
  VnAccordionContent,
  VnAccordionItem,
  VnAccordionTrigger,
} from "@/components/VnAccordion";
import { VnBox } from "@/components/VnBox";
import { VnCard } from "@/components/VnCard";
import { BaseCheckboxInput } from "@/components/VnCheckboxInput/BaseCheckboxInput";
import { VnCheckboxInput } from "@/components/VnCheckboxInput/VnCheckboxInput";
import { VnForm, VnFormLabel } from "@/components/VnForm";
import { VnText } from "@/components/VnText/VnText";
import { useListEligibilityResultsQuery } from "@/data/queries/eligibilityResultsQueries";
import { useListHouseholdMembersQuery } from "@/data/queries/householdMembersQueries";
import { CreateApplicationFormType } from "@/domain/applications";
import { MemberSelectionRequestType } from "@/hooks/useApplicationForm";
import { useMarketplaceBrandLabel } from "@/hooks/useMarketplaceBrandLabel";
import {
  composeBrandLabel,
  formatMemberList,
  getProgramTypeLabel,
  groupResultsByBrand,
} from "@/utils/brandLabel";
import { formatHouseholdMemberName } from "@/utils/householdMember";

type ApplicationOptionPropsType = {
  applicationType: ApplicationTypeEnumType;
  isDisabled: boolean;
  onRequestMemberSelection: (request: MemberSelectionRequestType) => void;
};

const ApplicationOption: FC<ApplicationOptionPropsType> = ({
  applicationType,
  isDisabled,
  onRequestMemberSelection,
}) => {
  const { data: eligibilityResultsData } = useListEligibilityResultsQuery({
    applicationType,
  });

  const { data: householdMembersData } = useListHouseholdMembersQuery();

  const form = useFormContext<CreateApplicationFormType>();
  const { control } = form;

  const householdMembers = form.watch("householdMembers");
  const isSectionSelected = householdMembers && householdMembers.length > 0;

  const marketplaceBrandLabel = useMarketplaceBrandLabel();

  const brandLabel = eligibilityResultsData?.length
    ? composeBrandLabel(eligibilityResultsData, applicationType)
    : applicationType === "MARKETPLACE"
      ? marketplaceBrandLabel
      : getProgramTypeLabel(applicationType);

  const brandGroups = eligibilityResultsData?.length
    ? groupResultsByBrand(eligibilityResultsData, applicationType)
    : [];

  const handleSectionCheckboxChange = (checked: boolean) => {
    if (isDisabled) return;

    onRequestMemberSelection({
      applicationType,
      memberIds: checked
        ? (householdMembersData?.map(member => member.id) ?? [])
        : [],
      isSectionToggle: true,
    });
  };

  return (
    <VnCard className="gap-4 rounded-md p-4">
      <VnAccordionItem value={applicationType} disabled={isDisabled}>
        <VnAccordionTrigger
          className="flex w-full flex-row gap-1.5 py-0"
          disabled={isDisabled}>
          <VnBox className="flex min-w-0 flex-1 flex-row gap-x-2.5">
            <BaseCheckboxInput
              checked={isSectionSelected}
              disabled={isDisabled}
              onCheckedChange={handleSectionCheckboxChange}
            />

            <VnBox className="-mt-0.5 flex min-w-0 flex-1 shrink flex-col gap-1.5">
              <VnFormLabel variant="lgBold">{brandLabel}</VnFormLabel>
              {eligibilityResultsData?.length ? (
                <VnBox className="gap-0.5">
                  {brandGroups.map(group => (
                    <VnText
                      key={group.brand}
                      className="text-muted-foreground"
                      i18nKey="native:applicationForm.mayBeEligibleFor"
                      i18nOptions={{
                        memberNames: formatMemberList({
                          names: group.memberNames,
                        }),
                        brand: group.brand,
                      }}
                    />
                  ))}
                </VnBox>
              ) : (
                <VnText
                  i18nKey="native:applicationForm.notEligible"
                  className="text-muted-foreground"
                />
              )}
            </VnBox>
          </VnBox>
        </VnAccordionTrigger>
        <VnAccordionContent className="pb-0">
          <VnBox className="bg-background mt-4 flex flex-col gap-2 rounded-sm p-4">
            <VnText i18nKey="common:whoIsApplying" variant="mdMedium" />

            <VnBox className="mt-2 gap-4">
              <VnCheckboxInput
                control={control}
                isDisabled={isDisabled}
                isRequired={false}
                inputClassName="bg-card"
                name="householdMembers"
                hasMultipleOptions
                // Always defers to the form hook, which owns conflict detection
                // and is the single writer for every program selection
                onBeforeOptionToggle={nextValues => {
                  onRequestMemberSelection({
                    applicationType,
                    memberIds: nextValues,
                  });
                  return false;
                }}
                options={
                  householdMembersData?.map(member => ({
                    label: formatHouseholdMemberName(member, "fullName"),
                    value: member.id,
                  })) ?? []
                }
              />
            </VnBox>
          </VnBox>
        </VnAccordionContent>
      </VnAccordionItem>
    </VnCard>
  );
};

type ApplicationFormAccordionsPropsType = {
  medicaidForm: ReturnType<typeof useForm<CreateApplicationFormType>>;
  medicareForm: ReturnType<typeof useForm<CreateApplicationFormType>>;
  marketplaceForm: ReturnType<typeof useForm<CreateApplicationFormType>>;
  openAccordions: string[];
  handleValueChange: (value: string[]) => void;
  isDisabled: boolean;
  onRequestMemberSelection: (request: MemberSelectionRequestType) => void;
};

export const ApplicationFormAccordions: FC<
  ApplicationFormAccordionsPropsType
> = ({
  medicaidForm,
  medicareForm,
  marketplaceForm,
  openAccordions,
  handleValueChange,
  isDisabled,
  onRequestMemberSelection,
}) => {
  return (
    <VnAccordion
      type="multiple"
      value={openAccordions}
      onValueChange={value => {
        if (!isDisabled) handleValueChange(value);
      }}
      className="flex w-full">
      <VnBox className="gap-y-5">
        <VnForm form={medicaidForm} isInputOnlyForm>
          <ApplicationOption
            applicationType="MEDICAID"
            isDisabled={isDisabled}
            onRequestMemberSelection={onRequestMemberSelection}
          />
        </VnForm>

        <VnForm form={medicareForm} isInputOnlyForm>
          <ApplicationOption
            applicationType="MEDICARE"
            isDisabled={isDisabled}
            onRequestMemberSelection={onRequestMemberSelection}
          />
        </VnForm>

        <VnForm form={marketplaceForm} isInputOnlyForm>
          <ApplicationOption
            applicationType="MARKETPLACE"
            isDisabled={isDisabled}
            onRequestMemberSelection={onRequestMemberSelection}
          />
        </VnForm>
      </VnBox>
    </VnAccordion>
  );
};
